import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const ChangePreferredPaymentMethodPopup = ({ selectedPlateId, isOpen, onRequestClose, loadPlateRenewalData }) => {
  const [activeTab, setActiveTab] = useState('ach');
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (isOpen) fetchPaymentMethods();
  }, [isOpen]);

  const fetchPaymentMethods = () => {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    fetch(`${API_URL}/user/payment-methods`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
      .then((data) => { setPaymentMethods(data); setLoading(false); })
      .catch((error) => console.error('Error fetching payment methods:', error));
  };

  const makePreferredMethod = (methodId) => {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    fetch(`${API_URL}/plate-renewals/${selectedPlateId}/update-payment-method`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: methodId }),
    })
      .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
      .then((data) => {
        toast.success('Preferred payment method updated successfully', { autoClose: 3000 });
        onRequestClose();
        loadPlateRenewalData();
      })
      .catch((error) => toast.error('Failed to set preferred payment method', { autoClose: 3000 }));
  };

  const maskAccountNumber = (fullAccountNumber) => {
    if (fullAccountNumber && fullAccountNumber.length >= 4) {
      return `••••${fullAccountNumber.slice(-4)}`;
    }
    return fullAccountNumber;
  };

  const filteredPaymentMethods = paymentMethods.filter(
    (method) =>
      (activeTab === 'ach' && method.payment_method.toLowerCase() === 'ach') ||
      (activeTab === 'creditCard' && method.payment_method.toLowerCase() === 'creditcard')
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-xl tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto tw-p-6 tw-outline-none"
      overlayClassName="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-[9999]"
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0">Choose Payment Method</h2>
        <button onClick={onRequestClose} className="tw-text-gray-400 hover:tw-text-gray-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-1">
          <FontAwesomeIcon icon={faTimes} className="tw-text-lg" />
        </button>
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-gap-1 tw-bg-gray-200 tw-rounded-lg tw-p-1 tw-mb-5">
        <button
          onClick={() => setActiveTab('ach')}
          className={`tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
            activeTab === 'ach' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
          }`}
        >
          ACH / eCheck
        </button>
        <button
          onClick={() => setActiveTab('creditCard')}
          className={`tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
            activeTab === 'creditCard' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
          }`}
        >
          Credit / Debit Card
        </button>
      </div>

      {/* Table */}
      <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200">
        <table className="tw-w-full">
          <thead>
            <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
              {activeTab === 'creditCard' ? (
                <>
                  <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Card Holder</th>
                  <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Card Number</th>
                </>
              ) : (
                <>
                  <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Account Number</th>
                  <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Routing Number</th>
                </>
              )}
              <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan="3" className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-gray-500">Loading...</td>
              </tr>
            ) : filteredPaymentMethods.length === 0 ? (
              <tr>
                <td colSpan="3" className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-gray-500">
                  No {activeTab === 'ach' ? 'ACH' : 'credit card'} payment methods available
                </td>
              </tr>
            ) : (
              filteredPaymentMethods.map((method, index) => (
                <tr key={index} className="hover:tw-bg-gray-50 tw-transition-colors">
                  {activeTab === 'creditCard' ? (
                    <>
                      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">{method.card_holder_name || '-'}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-gray-700">{maskAccountNumber(method.masked_card_number) || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-gray-700">{maskAccountNumber(method.account_number) || '-'}</td>
                      <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-gray-700">{method.routing_number || '-'}</td>
                    </>
                  )}
                  <td className="tw-px-4 tw-py-3 tw-text-right">
                    <button
                      onClick={() => makePreferredMethod(method.id)}
                      className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer"
                    >
                      Choose
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tw-mt-4 tw-text-center">
        <Link to="/payment-methods" className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-no-underline hover:tw-underline" onClick={onRequestClose}>
          + Add a new payment method
        </Link>
      </div>
    </Modal>
  );
};

export default ChangePreferredPaymentMethodPopup;
